import type {
	Cart,
	CentPrecisionMoney,
	ShippingMethod,
	TaxedPrice,
	TypedMoney,
} from "@commercetools/platform-sdk";

import { moneyToNumber } from "@babybjorn/utils/commercetools/money";
import { fetchCartById } from "../commercetools/cart-service";
import { getShippingMethodsForCart } from "../commercetools/shipping-methods-service";
import { notUndefined } from "../type-guards/general";
import { formatMoney } from "./currency";
import { getUpsShippingMethodDescription } from "@babybjorn/utils/cart/shippingDescription";

export type SelectableShipping = {
	label: string;
	value: string;
	id: string;
	money: TypedMoney;
	upsShippingMethod?: CustomShippingService;
	flatrateShippingMethod?: ShippingMethod;
	formattedPrice?: string;
	freeShipping?: boolean;
	key?: string;
	description?: string;
	guaranteedDelivery?: number;
};

interface CustomShippingService {
	code: string;
	name: string;
	price: CentPrecisionMoney;
	guaranteedDelivery?: number;
}

const mapShippingMethodToSelectableShipping = (
	shipping: ShippingMethod,
	shippingIsFree: boolean,
): SelectableShipping | undefined => {
	const language = process.env.GATSBY_LANGUAGE;
	if (!language) {
		return undefined;
	}
	const name =
		shipping.localizedName?.[language] ||
		shipping.localizedDescription?.[language] ||
		shipping.name;
	const price = shipping.zoneRates
		.map(
			(zoneRate) =>
				zoneRate.shippingRates.find((shippingRate) => shippingRate.isMatching)
					?.price,
		)
		.find(Boolean);

	if (!price) {
		throw new Error("Shipping method is missing price");
	}
	const formattedPrice = formatMoney(price);

	const label = name;
	return {
		label,
		value: shipping.id,
		id: shipping.id,
		flatrateShippingMethod: shipping,
		money: price,
		formattedPrice: shippingIsFree ? "" : formattedPrice,
		key: shipping.key ?? "default",
		description: shipping.localizedDescription?.[language],
		freeShipping: shippingIsFree,
	};
};

export const mapSelectedShippingMethodToSelectableShipping = (
	shipping: ShippingMethod,
	shippingPrice?: TaxedPrice,
): SelectableShipping | undefined => {
	const language = process.env.GATSBY_LANGUAGE;
	if (!language) {
		return undefined;
	}

	const name =
		shipping.localizedName?.[language] ||
		shipping.localizedDescription?.[language] ||
		shipping.name;
	const price = shippingPrice?.totalGross;

	if (!price) {
		throw new Error("Shipping method is missing price");
	}

	const formattedPrice = formatMoney(price);

	const label = name;
	return {
		label,
		value: shipping.id,
		id: shipping.id,
		money: price,
		formattedPrice,
		key: shipping.key ?? "default",
		freeShipping: moneyToNumber(price) === 0,
	};
};

export const getLowestFreeShippingAboveAmount = (
	shippingMethods: ShippingMethod[],
): number | undefined => {
	return shippingMethods
		.flatMap((shippingMethod) =>
			shippingMethod.zoneRates.flatMap((zoneRate) =>
				zoneRate.shippingRates
					.filter((shippingRate) => shippingRate.isMatching)
					.map((shippingRate) => moneyToNumber(shippingRate.freeAbove)),
			),
		)
		.sort((a, b) => {
			if (!a || !b) return 0;
			return a - b;
		})[0];
};

const hasMethodFreeShipping = (
	cart: Cart | undefined,
	method: ShippingMethod,
) => {
	if (!cart) return false;

	const hasFreeShippingLimit = remainingUntilFreeShipping(cart, method);
	if (hasFreeShippingLimit === 0) return true;
	return false;
};

const remainingUntilFreeShipping = (cart: Cart, method: ShippingMethod) => {
	const lowestFreeAbove = getLowestFreeShippingAboveAmount([method]);
	const hasFreeShippingLimit = lowestFreeAbove !== undefined;

	if (hasFreeShippingLimit) {
		return Math.max(amountRemainingUntilFreeShipping(lowestFreeAbove, cart), 0);
	}

	return;
};
export const amountRemainingUntilFreeShipping = (
	freeAbove: number,
	cart?: Cart | null,
): number => {
	return (
		freeAbove -
		moneyToNumber(cart?.totalPrice) +
		moneyToNumber(cart?.shippingInfo?.price)
	);
};

const shippingMethodToSelectable = (
	method: CustomShippingService,
): SelectableShipping => {
	const { name, price, code } = method;

	const formattedPrice = formatMoney(price);

	return {
		label: name,
		value: name,
		id: code,
		money: price,
		upsShippingMethod: method,
		formattedPrice,
		key: "ups",
		description: getUpsShippingMethodDescription(
			code,
			process.env.GATSBY_LANGUAGE || "en-US",
		),
		freeShipping: moneyToNumber(price) === 0,
	};
};

/**
 * This is needed since we need to be able to fetch shipping methods outside of the react hook context in the express payment callbacks.
 */
export const getShippingMethodsForStore = async (
	store: string,
): Promise<SelectableShipping[]> => {
	const upsStores = ["us", "ca"];
	const isUpsStore = upsStores.includes(store);
	const isAuStore = store === "au";
	const isFlatrateShippingStore = !isAuStore && !isUpsStore;
	const cartId = localStorage.getItem("CT_CART_ID");
	if (!cartId) return [];

	const cart = await fetchCartById(cartId);

	if (isFlatrateShippingStore) {
		const shippingMethods = await getShippingMethodsForCart(cart);

		return shippingMethods
			.map((shipping) => {
				return mapShippingMethodToSelectableShipping(
					shipping,
					hasMethodFreeShipping(cart, shipping),
				);
			})
			.filter(notUndefined);
	} else if (isAuStore) {
		let selectedShipping: SelectableShipping;
		if (cart.shippingInfo?.shippingMethodName) {
			const price = cart.taxedShippingPrice?.totalGross;
			const priceLabel = price
				? formatMoney(cart.taxedShippingPrice.totalGross)
				: "";

			if (!price) {
				throw new Error("Shipping method is missing price");
			}

			selectedShipping = {
				value: cart.shippingInfo.shippingMethodName,
				label: cart.shippingInfo.shippingMethodName,
				id: cart.shippingInfo.shippingMethodName,
				money: price,
				formattedPrice: priceLabel,
				key: "ap",
				description: "Orders ship in 1-2 business days and arrive in 5-7.",
				freeShipping: price.centAmount === 0,
			};
		} else {
			selectedShipping = {
				value: "",
				label: "",
				id: "",
				money: {
					centAmount: 0,
					currencyCode: "AUD",
					fractionDigits: 100,
					type: "centPrecision",
				},
			};
		}

		const shippingMethods = [selectedShipping];
		return shippingMethods;
	} else if (isUpsStore) {
		try {
			return (
				JSON.parse(
					String(cart.custom.fields.upsShippingRates) || "[]",
				) as CustomShippingService[]
			)
				.sort((a, b) => a.price.centAmount - b.price.centAmount)
				.map(shippingMethodToSelectable);
		} catch {
			return [];
		}
	} else {
		return [];
	}
};
